import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  AreaChart, Area, BarChart, Bar,
  XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import {
  getDashboardStats, getTimeSeries, getEngagementStats, getPowerUsers, getMissingWelcomeCredits,
  type DashboardStats, type TimeSeriesPoint, type FunnelCohort, type PowerUsersData, type PowerUser, type MissingWelcomeCredits,
} from "@/api/adminApi";
import { StatCard } from "@/components/StatCard";
import { RangeSelector } from "@/components/RangeSelector";
import { useRange } from "@/hooks/useRange";

function formatCurrency(cents: number): string {
  return "$" + (cents / 100).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatNumber(n: number): string {
  return n.toLocaleString("en-US");
}

const tooltipStyle = {
  contentStyle: { background: "#161616", border: "1px solid #6B5F44", borderRadius: 4 },
  labelStyle: { color: "#9A8E7A", fontSize: 11 },
  itemStyle: { color: "#F0E8D8" },
};

export function DashboardPage() {
  const navigate = useNavigate();
  const [rangeDays, setRangeDays] = useRange(90);
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [series, setSeries] = useState<TimeSeriesPoint[]>([]);
  const [funnel, setFunnel] = useState<FunnelCohort[]>([]);
  const [engagementExtras, setEngagementExtras] = useState<{ purchases_in_range: number; distinct_buyers_in_range: number } | null>(null);
  const [missingCredits, setMissingCredits] = useState<MissingWelcomeCredits | null>(null);
  const [powerUsers, setPowerUsers] = useState<PowerUsersData | null>(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      getDashboardStats(),
      getTimeSeries("new_users", rangeDays),
      getEngagementStats(rangeDays),
      getPowerUsers(5),
      getMissingWelcomeCredits(),
    ]).then(([statsRes, seriesRes, engagementRes, powerRes, missing]) => {
      setStats(statsRes.stats);
      setSeries(seriesRes.series);
      setFunnel(engagementRes.funnel);
      setEngagementExtras({
        purchases_in_range: engagementRes.purchases_in_range,
        distinct_buyers_in_range: engagementRes.distinct_buyers_in_range,
      });
      setPowerUsers(powerRes);
      setMissingCredits(missing);
      setLastUpdated(new Date());
    }).finally(() => setLoading(false));
  }, [rangeDays]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="loading">Loading feature presentation...</div>;
  if (!stats) return <div className="empty-state">No features playing</div>;

  // Aggregate funnel across all cohorts
  const funnelTotals = funnel.reduce(
    (acc, c) => ({
      signups: acc.signups + c.signups,
      first_gen: acc.first_gen + c.first_gen,
      second_gen: acc.second_gen + c.second_gen,
      first_purchase: acc.first_purchase + c.first_purchase,
    }),
    { signups: 0, first_gen: 0, second_gen: 0, first_purchase: 0 },
  );

  const funnelData = funnelTotals.signups > 0
    ? [
        { name: "Signups", value: funnelTotals.signups },
        { name: "1st Gen", value: funnelTotals.first_gen },
        { name: "2nd Gen", value: funnelTotals.second_gen },
        // Buyers = cohort members who bought. Separate "Purchases" card below shows all purchases in range.
        { name: "Buyers", value: funnelTotals.first_purchase },
      ]
    : [];

  const hasMissing = (missingCredits?.total ?? 0) > 0;

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12, flexWrap: "wrap" }}>
        <RangeSelector value={rangeDays} onChange={setRangeDays} />
        <button className="btn-outline" onClick={load} disabled={loading}>
          {loading ? "Refreshing..." : "Refresh"}
        </button>
        {lastUpdated && (
          <span style={{ fontSize: 11, color: "#9A8E7A" }}>
            Updated {lastUpdated.toLocaleTimeString()}
          </span>
        )}
      </div>

      <div className="grid-2x3">
        {/* All six tiles are ALL-TIME and ignore the range selector; the range applies to the
            cards and charts below. Labels say what the SQL counts (admin_dashboard_stats). */}
        <StatCard label="Accounts (all time)" value={formatNumber(stats.total_users)} sub="auth.users minus anonymous reviewer/upload accounts" />
        <StatCard label="Signed In (last 7d)" value={formatNumber(stats.active_users_7d)} sub="accounts with an auth session in 7d — not usage" />
        <StatCard label="Credit Revenue (all time)" value={formatCurrency(stats.total_revenue_cents)} sub="credit packs only; subscriptions live in App Store Connect" />
        <StatCard label="Credits Purchased (all time)" value={formatNumber(stats.credits_purchased)} sub="from completed pack purchases" />
        <StatCard label="Credits Used (all time)" value={formatNumber(stats.credits_used)} sub="includes welcome and reward credits" />
        <StatCard label="AI Jobs (all time)" value={formatNumber(stats.total_generations)} sub="every ai_jobs row, any status" />
      </div>

      {/* Range-scoped buyer signals */}
      <div className="grid-2" style={{ marginTop: 16 }}>
        <StatCard
          label={`Pack Purchases (${rangeDays}d)`}
          value={formatNumber(engagementExtras?.purchases_in_range ?? 0)}
          sub="completed credit-pack transactions"
        />
        <StatCard
          label={`Pack Buyers (${rangeDays}d)`}
          value={formatNumber(engagementExtras?.distinct_buyers_in_range ?? 0)}
          sub="distinct accounts that bought a pack"
        />
      </div>

      {/* Auto-hides once the in-app welcome-credit grant is fixed. */}
      {hasMissing && (
        <div
          onClick={() => navigate("/users?missing_welcome_credits=1")}
          style={{
            marginTop: 16,
            padding: "10px 14px",
            background: "#2a1810",
            border: "1px solid #6B3A1A",
            borderRadius: 4,
            fontSize: 12,
            color: "#F0E8D8",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <span style={{ color: "#E0A040", fontWeight: 600 }}>⚠</span>
          <span>
            <strong>{formatNumber(missingCredits!.total)}</strong> users missing welcome credits
            {missingCredits!.last_30d > 0 && <> ({formatNumber(missingCredits!.last_30d)} in last 30d)</>}
            &nbsp;— click to view.
          </span>
        </div>
      )}

      {/* New Users chart + Engagement Funnel */}
      <div className="grid-2" style={{ marginTop: 24 }}>
        <div className="card chart-card">
          <h3>New Users — Last {rangeDays} Days</h3>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={series}>
              <defs>
                <linearGradient id="goldGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#D4AF37" stopOpacity={0.3} />
                  <stop offset="100%" stopColor="#D4AF37" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#4A454033" />
              <XAxis
                dataKey="date"
                tickFormatter={(d: string) => new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                tick={{ fontSize: 10, fill: "#9A8E7A" }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "#9A8E7A" }} axisLine={false} tickLine={false} />
              <Tooltip
                {...tooltipStyle}
                labelFormatter={(d: string) => new Date(d).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
              />
              <Area type="monotone" dataKey="value" stroke="#D4AF37" strokeWidth={2} fill="url(#goldGrad)" name="New Users" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {funnelData.length > 0 && (
          <div className="card chart-card">
            <h3>Signup Cohort Funnel ({rangeDays}d)</h3>
            <p style={{ fontSize: 11, color: "#9A8E7A", margin: "0 0 8px" }}>
              Accounts created in the range → ran ≥1 AI job → ran ≥2 → bought a pack. Account-based, all entitlements.
            </p>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={funnelData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#4A454033" />
                <XAxis type="number" allowDecimals={false} tick={{ fontSize: 10, fill: "#9A8E7A" }} axisLine={false} tickLine={false} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 10, fill: "#9A8E7A" }} axisLine={false} tickLine={false} width={80} />
                <Tooltip {...tooltipStyle} />
                <Bar dataKey="value" fill="#D4AF37" radius={[0, 4, 4, 0]} name="Users" />
              </BarChart>
            </ResponsiveContainer>
            {funnelTotals.signups > 0 && (
              <p style={{ fontSize: 11, color: "#9A8E7A", marginTop: 8, textAlign: "center" }}>
                {Math.round((funnelTotals.first_gen / funnelTotals.signups) * 100)}% generate
                &middot; {Math.round((funnelTotals.first_purchase / funnelTotals.signups) * 100)}% became buyers
              </p>
            )}
          </div>
        )}
      </div>

      {/* Power Users */}
      {powerUsers && (
        <>
          <h3 className="section-title" style={{ marginTop: 12 }}>Power Users</h3>
          <div className="grid-2">
            <LeaderboardCard title="Top Generators" users={powerUsers.by_generations} formatValue={(v) => `${v} gens`} navigate={navigate} />
            <LeaderboardCard title="Top Spenders" users={powerUsers.by_spend} formatValue={(v) => `$${(v / 100).toFixed(2)}`} navigate={navigate} />
            <LeaderboardCard title="Most Shared" users={powerUsers.by_shares} formatValue={(v) => `${v} sessions`} navigate={navigate} />
            <LeaderboardCard title="Top Referrers" users={powerUsers.by_referrals} formatValue={(v) => `${v} referrals`} navigate={navigate} />
          </div>
        </>
      )}

      {/* Quick links */}
      <div style={{ display: "flex", gap: 12, marginTop: 20 }}>
        <button className="btn-outline" onClick={() => navigate("/users")}>
          Search Users &rarr;
        </button>
        <button className="btn-outline" onClick={() => navigate("/analytics")}>
          View Analytics &rarr;
        </button>
      </div>
    </div>
  );
}

function LeaderboardCard({
  title, users, formatValue, navigate,
}: {
  title: string;
  users: PowerUser[];
  formatValue: (v: number) => string;
  navigate: (path: string) => void;
}) {
  if (users.length === 0) return null;
  return (
    <div className="card chart-card">
      <h3>{title}</h3>
      <ul className="leaderboard-list">
        {users.slice(0, 5).map((u, i) => (
          <li key={u.user_id} className="leaderboard-item" onClick={() => navigate(`/users/${u.user_id}`)}>
            <span className="leaderboard-rank">{i + 1}.</span>
            <span className="leaderboard-name">{u.display_name || u.email}</span>
            <span className="leaderboard-value">{formatValue(u.value)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
